import React, { useCallback, useEffect, useState } from 'react';

import type { Theme } from '@mui/material';
import { Collapse, Paper, Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import { Spacer } from 'src/components';
import { StatusBadge } from 'src/components/admin/integrations/StatusBadge';
import { autoSyncParamKey } from 'src/components/ai/chat/ChatPanel/ChatEditor';
import { CRMConnectedIcon } from 'src/components/icons/CRMConnectedIcon';
import { Apollo } from 'src/gql/generated';
import { updateUrlWithoutRefresh } from 'src/helpers';
import { useAlineApiDeckSession } from 'src/hooks';
import { appColors } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import { FolderSyncJobsList } from './FolderSyncJobsList';
import {
  IntegrationActionButton,
  integrationHoverActionRevealStyles,
} from './IntegrationActionButton';

export function integrationCardStyles(theme: Theme) {
  return {
    backgroundColor: '#FFF',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1.5),
    boxShadow: 'none',
  };
}

export function integrationIconStyles(theme: Theme) {
  return {
    checkMark: {
      bottom: theme.spacing(-0.75),
      height: theme.spacing(2),
      position: 'absolute',
      right: theme.spacing(-0.75),
      width: theme.spacing(2),
    },
    icon: {
      display: 'block',
      height: theme.spacing(3),
      width: theme.spacing(3),
    },
    iconBox: {
      alignItems: 'center',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.spacing(1),
      display: 'flex',
      flexShrink: 0,
      height: theme.spacing(5),
      justifyContent: 'center',
      position: 'relative',
      width: theme.spacing(5),
    },
  } satisfies Record<string, React.CSSProperties>;
}

export const integrationNameStyles = {
  color: appColors.strongType,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 'normal',
} satisfies React.CSSProperties;

const useStyles = makeStyles()(theme => ({
  checkMark: integrationIconStyles(theme).checkMark,
  compactCheckMark: {
    bottom: theme.spacing(-0.5),
    height: theme.spacing(2),
    left: theme.spacing(2),
    position: 'absolute',
    width: theme.spacing(2),
  },
  compactIcon: {
    display: 'block',
    height: 26,
    width: 26,
  },
  compactImageContainer: {
    display: 'inline-block',
    position: 'relative',
  },
  description: {
    flexGrow: 1,
    marginLeft: theme.spacing(1),
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    padding: theme.spacing(1.5),
  },
  icon: integrationIconStyles(theme).icon,
  imageContainer: integrationIconStyles(theme).iconBox,
  integrationDisplay: {
    alignItems: 'center',
    display: 'flex',
  },
  name: integrationNameStyles,
  paper: {
    ...integrationCardStyles(theme),
    ...integrationHoverActionRevealStyles,
    overflow: 'hidden',
  },
}));

export interface IntegrationDisplayProps {
  imageUrl: string;
  isConnected: boolean;
  name: string;
  className?: string;
  iconClassName?: string;
  /** Holds the "Connected" line's height with a skeleton while the state is still loading. */
  statusLoading?: boolean;
  variant?: 'admin';
}

export function IntegrationDisplay({
  className,
  iconClassName,
  imageUrl,
  isConnected,
  name,
  statusLoading = false,
  variant,
}: IntegrationDisplayProps): JSX.Element {
  const { classes } = useStyles();
  const isAdmin = variant === 'admin';
  const iconBoxClass = isAdmin ? classes.imageContainer : classes.compactImageContainer;
  const iconClass = iconClassName ?? (isAdmin ? classes.icon : classes.compactIcon);
  const checkMarkClass = isAdmin ? classes.checkMark : classes.compactCheckMark;

  return (
    <div className={clsx(classes.integrationDisplay, className)}>
      <div className={iconBoxClass}>
        <img alt={name} className={iconClass} src={imageUrl} />
        {isConnected && <CRMConnectedIcon className={checkMarkClass} />}
      </div>
      <div className={classes.description}>
        <Typography
          className={isAdmin ? classes.name : undefined}
          variant={isAdmin ? undefined : 'subtitle2'}
        >
          {name}
        </Typography>
        <Collapse unmountOnExit in={isAdmin && (isConnected || statusLoading)} timeout={180}>
          {statusLoading && <Skeleton height={18} variant="text" width={64} />}
          {!statusLoading && (
            <StatusBadge
              hideIcon
              data-cy="crm-status-connected"
              label="Connected"
              status="success"
            />
          )}
        </Collapse>
        {isConnected && !isAdmin && (
          <Typography color="secondary" variant="caption">
            Connected
          </Typography>
        )}
      </div>
    </div>
  );
}

export interface IntegrationRowProps extends React.HTMLAttributes<HTMLElement> {
  connections: FileStorageConnection[];
  imageUrl: string;
  integration: Apollo.FileStorageIntegration;
  name: string;
  state: Apollo.ApiDeckConnectionState | null;
  stateLoading?: boolean;
  statusLoading?: boolean;
}

export function IntegrationRow({
  connections,
  imageUrl,
  integration,
  name,
  state,
  stateLoading = false,
  statusLoading = false,
}: IntegrationRowProps): JSX.Element {
  const { classes } = useStyles();
  const { openVault } = useFileStorageSession(connections);
  const isConnectionCallable = state === Apollo.ApiDeckConnectionState.Callable;

  useEffect(() => {
    /**
     * Rows drawn from the cached provider list have no connection state yet. Deciding on a null
     * state would open the Vault for a provider that is already connected, and consuming the
     * param here would stop the real state from ever being acted on.
     */
    if (stateLoading) return;

    const searchParams = new URLSearchParams(window.location.search);
    const autoSyncParam = searchParams.get(autoSyncParamKey);
    if (!autoSyncParam) return;

    const integrationMatchesParam = autoSyncParam === getServiceIdFromIntegration(integration);
    // Ensure only the matching integration processes the auto-sync param to avoid race conditions.
    if (!integrationMatchesParam) return;

    // Auto-open the Vault when this integration matches URL param and the integration is not connected.
    if (!isConnectionCallable) openVault(getServiceIdFromIntegration(integration));

    const url = new URL(window.location.href);
    url.searchParams.delete(autoSyncParamKey);
    updateUrlWithoutRefresh(url.toString());
  }, [integration, isConnectionCallable, openVault, stateLoading]);

  return (
    <Paper className={classes.paper} elevation={0}>
      <div className={classes.header}>
        <IntegrationDisplay
          imageUrl={imageUrl}
          isConnected={isConnectionCallable}
          name={name}
          statusLoading={statusLoading}
          variant="admin"
        />
        <Spacer />
        {stateLoading && (
          <Skeleton height={36} sx={{ borderRadius: 28 }} variant="rounded" width={96} />
        )}
        {!stateLoading && (
          <IntegrationActionButton
            onClick={() => openVault(getServiceIdFromIntegration(integration))}
          >
            {isConnectionCallable ? 'Manage' : 'Connect'}
          </IntegrationActionButton>
        )}
      </div>
      <FolderSyncJobsList
        connections={connections}
        integration={integration}
        state={state}
        stateLoading={stateLoading}
      />
    </Paper>
  );
}

export function IntegrationRowSkeleton(): JSX.Element {
  const { classes } = useStyles();

  return (
    <Paper className={classes.paper} elevation={0}>
      <div className={classes.header}>
        <div className={classes.imageContainer}>
          <Skeleton className={classes.icon} variant="rectangular" />
        </div>
        <div className={classes.description}>
          <Skeleton height={24} variant="text" width="20%" />
          <Skeleton height={16} variant="text" width="10%" />
        </div>
      </div>
    </Paper>
  );
}

export type FileStorageConnection = Pick<
  Apollo.FileStorageConnection,
  'serviceId' | 'state' | '__typename'
>;

export function useFileStorageSession(connections: FileStorageConnection[]) {
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    serviceId: Apollo.FileStorageIntegration | null;
  }>({
    open: false,
    serviceId: null,
  });
  const [getStorageConnections] = Apollo.useGetFileStorageConnectionsLazyQuery({
    fetchPolicy: 'network-only',
  });
  const { openVault } = useAlineApiDeckSession({
    onClose: getStorageConnections,
    unifiedApi: 'file-storage',
  });
  const openVaultOrFolderPicker = useCallback(
    async (integration: Apollo.FileStorageIntegration) => {
      const connection = connections.find(connection => connection.serviceId === integration);
      if (!connection || connection.state !== Apollo.ApiDeckConnectionState.Callable)
        return openVault(getServiceIdFromIntegration(integration));

      setDialogState({
        open: true,
        serviceId: integration,
      });
    },
    [connections, openVault]
  );

  return { dialogState, openVault, openVaultOrFolderPicker, setDialogState };
}

export function getServiceIdFromIntegration(integration: Apollo.FileStorageIntegration) {
  switch (integration) {
    case Apollo.FileStorageIntegration.GoogleDrive:
      return 'google-drive';
    case Apollo.FileStorageIntegration.OneDrive:
      return 'onedrive';
    case Apollo.FileStorageIntegration.Dropbox:
      return 'dropbox';
    case Apollo.FileStorageIntegration.Box:
      return 'box';
    case Apollo.FileStorageIntegration.SharePoint:
      return 'sharepoint';
  }
}
